/**
 * App
 * Root component for kevinjlope.dev v2.
 *
 * - BrowserRouter routes via lazy-loaded pages from src/pages/
 * - 11 routes + 404
 * - ChatProvider + ThemeProvider + Suspense wrap all routes
 * - Header rendered globally; Footer (slim PageFooter) rendered globally
 */

import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import ReactGA from "react-ga4";

import Header from "./Components/Header";
import Footer from "./Components/Footer";
import { ChatProvider } from "./context/ChatContext";
import { ThemeProvider } from "./context/ThemeContext";

const Home = lazy(() => import("./pages/Home"));
const About = lazy(() => import("./pages/About"));
const Experience = lazy(() => import("./pages/Experience"));
const Work = lazy(() => import("./pages/Work"));
const WorkDetail = lazy(() => import("./pages/WorkDetail"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Now = lazy(() => import("./pages/Now"));
const Talks = lazy(() => import("./pages/Talks"));
const KevinBot = lazy(() => import("./pages/KevinBot"));
const Contact = lazy(() => import("./pages/Contact"));
const NotFound = lazy(() => import("./pages/NotFound"));

import "./styles.css";

function LoadingFallback() {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex items-center justify-center p-20 min-h-[40vh]"
    >
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" />
        <span className="font-mono text-xs uppercase tracking-[0.3em] text-tertiary">
          {t("common.loading", { defaultValue: "Loading" })}
        </span>
      </div>
    </div>
  );
}

/** Send a GA pageview on every client-side route change. */
function RouteChangeTracker() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    ReactGA.send({ hitType: "pageview", page: pathname });
  }, [pathname]);
  return null;
}

function Shell() {
  return (
    <div id="main" className="overflow-x-hidden">
      <Header />
      <main id="main-content">
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/experience" element={<Experience />} />
            <Route path="/work" element={<Work />} />
            <Route path="/work/:slug" element={<WorkDetail />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/now" element={<Now />} />
            <Route path="/talks" element={<Talks />} />
            <Route path="/kevin-bot" element={<KevinBot />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <ChatProvider>
          <RouteChangeTracker />
          <Shell />
        </ChatProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
